<template>
	<section class="new-event-transfer-reservation">
		<wait-modal id="new-transfer-wait-modal" ref="waitModal" :title="titleLabel"></wait-modal>
		<conclusion-modal id="new-transfer-conclusion-modal" ref="conclusionModal"></conclusion-modal>
		<conclusion-bank-ticket-modal ref="conclusionBankTicketModal"></conclusion-bank-ticket-modal>
		<invalid-credit-card-modal ref="invalidPaymentModal"></invalid-credit-card-modal>

		<div class="new-event-transfer-reservation__inner">
			<h2>{{titleLabel}}</h2>

			<b-form class="new-event-transfer-reservation__form" @submit.prevent="searchTransferServices">
				<section class="new-event-transfer-reservation__panel new-event-transfer-reservation__panel--holder">
					<button
						type="button"
						class="new-event-transfer-reservation__toggle"
						:aria-label="translation.responsible"
					>
						<span></span>
					</button>

					<h3>{{translation.responsible}}</h3>

					<div class="new-event-transfer-reservation__holder-grid">
						<div class="new-event-transfer-reservation__field new-event-transfer-reservation__field--full">
							<b-form-input
								v-model="fileRequest.pax.name"
								class="new-event-transfer-reservation__control"
								type="text"
								:placeholder="transferFullName"
								required
							/>
						</div>

						<div class="new-event-transfer-reservation__field">
							<b-form-input
								v-model="fileRequest.pax.email"
								class="new-event-transfer-reservation__control"
								type="email"
								:placeholder="readableLabel(translation.email)"
								required
							/>
						</div>

						<div class="new-event-transfer-reservation__field new-event-transfer-reservation__field--select">
							<b-form-select
								v-model="fileRequest.pax.country.id"
								class="new-event-transfer-reservation__control new-event-transfer-reservation__select"
								:options="countryOptions"
								required
							/>
						</div>
					</div>
				</section>

				<section ref="searchPanel" class="new-event-transfer-reservation__panel new-event-transfer-reservation__panel--search">
					<h3>{{readableLabel(translation.searchTitle)}}</h3>

					<div class="new-event-transfer-reservation__search-row">
						<label for="new-transfer-guests">{{translation.paxNumber}}</label>
						<div class="new-event-transfer-reservation__passengers">
							<b-form-select
								id="new-transfer-guests"
								v-model="search.guestsQty"
								class="new-event-transfer-reservation__control new-event-transfer-reservation__select"
								:options="passengerOptions"
								required
							/>
						</div>

						<label>{{translation.date}}</label>
						<date-picker
							v-model="date"
							class="new-event-transfer-reservation__date"
							:initialDefaultDate="initialDefaultDate"
							required
							@input="updateFormattedDate"
						/>

						<button type="submit" class="new-event-transfer-reservation__button">
							{{lowercaseLabel(translation.search)}}
						</button>
					</div>
				</section>
			</b-form>

			<section v-if="hasSearched" class="new-event-transfer-reservation__results">
				<h3>{{translation.resultTitle || resultTitle}}</h3>

				<div v-if="transfers.length" class="new-event-transfer-reservation__result-list">
					<div
						v-for="transfer in transfers"
						:key="transfer.id"
						class="new-event-transfer-reservation__result-item"
						:class="{'new-event-transfer-reservation__result-item--open': isSelectedTransfer(transfer)}"
					>
						<article class="new-event-transfer-reservation__result">
							<h4>{{transferName(transfer)}}</h4>

							<div class="new-event-transfer-reservation__price">
								<span>{{translation.detail.pricePerPerson}}</span>
								<strong>{{formatCurrency(priceFor(transfer).pricePerPax, priceFor(transfer).priceCurrency)}}</strong>
							</div>

							<div class="new-event-transfer-reservation__price">
								<span>{{translation.detail.price}}</span>
								<strong>{{formatCurrency(priceFor(transfer).price, priceFor(transfer).priceCurrency)}}</strong>
							</div>

							<div class="new-event-transfer-reservation__result-action">
								<button
									type="button"
									class="new-event-transfer-reservation__select-button"
									:class="{'new-event-transfer-reservation__select-button--outline': isSelectedTransfer(transfer)}"
									@click="toggleTransferDetails(transfer)"
								>
									{{selectButtonLabel(transfer)}}
								</button>
							</div>
						</article>

						<b-form
							v-if="isSelectedTransfer(transfer) && bookingDetails"
							class="new-event-transfer-reservation__booking-details"
							@submit.prevent="addTransferBooking"
						>
							<button
								type="button"
								class="new-event-transfer-reservation__toggle new-event-transfer-reservation__booking-toggle"
								:aria-label="translation.detail.title"
							>
								<span></span>
							</button>

							<h3>{{translation.detail.title}}</h3>

							<div class="new-event-transfer-reservation__booking-layout">
								<div class="new-event-transfer-reservation__booking-fields">
									<div class="new-event-transfer-reservation__booking-grid">
										<b-row no-gutters class="new-event-transfer-reservation__booking-row">
											<b-col cols="12" lg="2" class="new-event-transfer-reservation__booking-field">
												<b-form-input
													v-model="bookingDetails.pickup"
													class="new-event-transfer-reservation__control"
													type="text"
													:placeholder="translation.detail.hour"
													v-mask="'99:99'"
													required
												/>
											</b-col>
										</b-row>

										<b-row no-gutters class="new-event-transfer-reservation__booking-row">
											<b-col cols="12" lg="6" class="new-event-transfer-reservation__booking-field">
												<b-form-input
													v-model="bookingDetails.pickupPlace"
													class="new-event-transfer-reservation__control"
													type="text"
													:placeholder="translation.detail.origin"
													required
												/>
											</b-col>

											<b-col cols="12" lg="6" class="new-event-transfer-reservation__booking-field">
												<b-form-input
													v-model="bookingDetails.dropoffPlace"
													class="new-event-transfer-reservation__control"
													type="text"
													:placeholder="translation.detail.destination"
													required
												/>
											</b-col>
										</b-row>

										<b-row no-gutters class="new-event-transfer-reservation__booking-row">
											<b-col cols="12" lg="6" class="new-event-transfer-reservation__booking-field">
												<b-form-input
													v-model="bookingDetails.flightCompany"
													class="new-event-transfer-reservation__control"
													type="text"
													:placeholder="translation.detail.flightCompany"
												/>
											</b-col>

											<b-col cols="12" lg="6" class="new-event-transfer-reservation__booking-field">
												<b-form-input
													v-model="bookingDetails.flightNumber"
													class="new-event-transfer-reservation__control"
													type="text"
													:placeholder="translation.detail.flightNumber"
												/>
											</b-col>
										</b-row>

										<b-row no-gutters class="new-event-transfer-reservation__booking-row">
											<b-col cols="12" class="new-event-transfer-reservation__booking-field">
												<b-form-textarea
													v-model="bookingDetails.observations"
													class="new-event-transfer-reservation__control new-event-transfer-reservation__textarea"
													:placeholder="translation.detail.observation"
												/>
											</b-col>
										</b-row>
									</div>

									<div class="new-event-transfer-reservation__passengers-details">
										<fieldset
											v-for="(guest, guestIndex) in bookingDetails.guests"
											:key="guestIndex"
											class="new-event-transfer-reservation__guest"
										>
											<legend>{{translation.detail.pax}} {{guestIndex + 1}}</legend>

											<b-row no-gutters class="new-event-transfer-reservation__guest-grid">
												<b-col cols="12" lg="8" class="new-event-transfer-reservation__guest-field">
													<b-form-input
														v-model="guest.name"
														class="new-event-transfer-reservation__control"
														type="text"
														:placeholder="translation.name"
														required
													/>
												</b-col>

												<b-col cols="12" lg="4" class="new-event-transfer-reservation__guest-field">
													<date-picker
														v-model="guest.birthDate"
														class="new-event-transfer-reservation__date new-event-transfer-reservation__booking-date"
														:placeholder="translation.detail.birthdate"
														:initialDefaultDate="initialDefaultBirthDate"
														required
													/>
												</b-col>
											</b-row>
										</fieldset>
									</div>
								</div>

								<div class="new-event-transfer-reservation__booking-action">
									<button type="submit" class="new-event-transfer-reservation__add-button">
										{{lowercaseLabel(translation.detail.add)}}
									</button>
								</div>
							</div>
						</b-form>
					</div>
				</div>

				<p v-else class="new-event-transfer-reservation__no-results">
					{{translation.noResult}}
				</p>
			</section>

			<div v-if="hasBookings" ref="checkout" class="new-event-transfer-reservation__checkout">
				<transfers-booking-resume
					:bookings="fileRequest.serviceBookingsRequest"
					:language="$store.state.language"
					@add-another="addAnotherTransfer"
					@remove-booking="removeTransferBooking"
				/>

				<credit-card-info
					:country="fileRequest.pax.country"
					:event="currentEvent()"
					:booking-response="bookingResponse"
					@book="sendBookings"
				/>
			</div>
		</div>
	</section>
</template>

<script>
import moment from 'moment';
import mask from 'awesome-mask';
import { sumBy } from 'lodash';
import { translate } from '../../assets/translations';
import DatePicker from '../shared/DatePicker.vue';
import BookingService from '../../services/BookingService';
import ConclusionBankTicketModal from '../shared/ConclusionBankTicketModal.vue';
import ConclusionModal from '../shared/ConclusionModal.vue';
import EventService from '../../services/EventService';
import FileRequest from '../../model/FileRequest';
import InvalidCreditCardModal from '../shared/InvalidCreditCardModal.vue';
import TransfersBookingResume from './TransfersBookingResume.vue';
import CreditCardInfo from '../shared/CreditCardInfo.vue';
import PaymentService from '../../services/PaymentService';
import Pax from '../../model/Pax';
import PaymentMethods from '../../enums/PaymentMethods';
import PaymentStatus from '../../enums/PaymentStatus';
import ServiceBooking from '../../model/ServiceBooking';
import ServicePricesWebservice from '../../services/ServicePricesWebservice';
import WaitModal from '../shared/WaitModal.vue';
import { getFormatedDate } from '../../util';

export default {
	directives: { mask },
	components: {
		ConclusionBankTicketModal,
		ConclusionModal,
		DatePicker,
		InvalidCreditCardModal,
		TransfersBookingResume,
		CreditCardInfo,
		WaitModal,
	},
	props: {
		event: {
			type: Object,
			required: true,
		},
	},
	data() {
		return {
			bookingResponse: null,
			countries: [],
			date: null,
			eventService: new EventService(),
			fileRequest: {
				pax: new Pax(),
				serviceBookingsRequest: [],
			},
			bookingDetails: null,
			hasSearched: false,
			paymentService: new PaymentService(),
			PaymentMethods,
			PaymentStatus,
			selectedTransfer: null,
			search: this.createSearch(),
			transfers: [],
		};
	},
	computed: {
		translation() {
			return translate(this.$store.state.language).transferReservation;
		},
		countryOptions() {
			const placeholder = {
				value: null,
				text: this.lowercaseLabel(this.translation.country),
				disabled: true,
			};
			const countries = this.countries.map(country => ({
				value: country.id,
				text: country.name,
			}));
			return [placeholder].concat(countries);
		},
		initialDefaultDate() {
			if (this.event && this.event.startDate) {
				return moment(this.event.startDate, 'YYYY-MM-DD').format('DD/MM/YYYY');
			}
			return moment().format('DD/MM/YYYY');
		},
		initialDefaultBirthDate() {
			return moment().format('DD/MM/YYYY');
		},
		initialDateValue() {
			if (this.event && this.event.startDate) {
				return moment(this.event.startDate, 'YYYY-MM-DD').toISOString();
			}
			return moment().toISOString();
		},
		passengerOptions() {
			return this.buildArrayOfNumbers(10);
		},
		resultTitle() {
			const label = this.translation.result || '';
			if (label.toLowerCase().indexOf('transfer') !== -1) return label;
			return `${label} transfers disponiveis`;
		},
		titleLabel() {
			return this.readableLabel(this.translation.title).replace(' De ', ' de ');
		},
		transferFullName() {
			return this.translation.fullName || this.readableLabel(this.translation.name);
		},
		hasBookings() {
			return Boolean(this.fileRequest.serviceBookingsRequest.length);
		},
	},
	created() {
		this.date = this.initialDateValue;
		this.updateFormattedDate();
		this.getCountries();
	},
	methods: {
		buildArrayOfNumbers(number) {
			let numberArray = [];
			for (let index = 1; index <= number; index++) {
				numberArray.push(index);
			}
			return numberArray;
		},
		createSearch() {
			const event = this.currentEvent();
			return {
				clientId: event && event.client ? event.client.id : null,
				cityId: event && event.destination ? event.destination.id : null,
				date: null,
				guestsQty: 1,
				serviceIds: [],
				publishedRatesOnly: false
			};
		},
		currentEvent() {
			return this.event || this.$store.state.event;
		},
		extractTransfersFromResponse(serviceTypes) {
			const eventTransferIds = this.search.serviceIds;
			const transfers = [];
			for (let typeIndex in serviceTypes) {
				let serviceType = serviceTypes[typeIndex];
				for (let serviceIndex in serviceType.services) {
					let service = serviceType.services[serviceIndex];
					if (eventTransferIds.indexOf(service.id) !== -1) {
						transfers.push(service);
					}
				}
			}
			return transfers;
		},
		configureSearch(eventTransfersIds) {
			const event = this.currentEvent();
			this.updateFormattedDate();
			this.search.clientId = event && event.client ? event.client.id : null;
			this.search.cityId = event && event.destination ? event.destination.id : null;
			this.search.serviceIds = eventTransfersIds;
			this.search.AdultsQty = this.search.guestsQty;
		},
		formatCurrency(value, currency) {
			const symbol = currency && currency.symbol ? currency.symbol : 'R$';
			const number = this.normalizeCurrencyValue(value).toLocaleString('pt-BR', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});
			return `${symbol} ${number}`;
		},
		normalizeCurrencyValue(value) {
			if (value === null || value === undefined || value === '') return 0;
			if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

			let normalizedValue = String(value).trim().replace(/[^\d,.-]/g, '');
			if (!normalizedValue) return 0;

			const lastComma = normalizedValue.lastIndexOf(',');
			const lastDot = normalizedValue.lastIndexOf('.');

			if (lastComma !== -1 && lastDot !== -1) {
				normalizedValue = lastComma > lastDot
					? normalizedValue.replace(/\./g, '').replace(',', '.')
					: normalizedValue.replace(/,/g, '');
			} else if (lastComma !== -1) {
				normalizedValue = normalizedValue.replace(',', '.');
			} else if (/^-?\d{1,3}(\.\d{3})+$/.test(normalizedValue)) {
				normalizedValue = normalizedValue.replace(/\./g, '');
			}

			const numberValue = Number(normalizedValue);
			return Number.isFinite(numberValue) ? numberValue : 0;
		},
		addTransferBooking() {
			if (!this.bookingDetails || !this.selectedTransfer) return;

			const booking = this.bookingDetails;
			booking.guests = booking.guests.map(guest => Object.assign({}, guest, {
				birthDate: getFormatedDate(guest.birthDate),
			}));

			this.fileRequest.serviceBookingsRequest.push(booking);
			this.$emit('add-to-bookings', {
				fileRequest: this.fileRequest,
				booking,
				transfer: this.selectedTransfer,
			});
			this.closeTransferDetails();
			this.hasSearched = false;
			this.transfers = [];
			this.$nextTick(() => this.scrollToElement(this.$refs.checkout));
		},
		addAnotherTransfer() {
			this.bookingResponse = null;
			this.hasSearched = false;
			this.transfers = [];
			this.closeTransferDetails();
			this.$nextTick(() => this.scrollToElement(this.$refs.searchPanel));
		},
		async authorizeCrediCardPayment(bookings, sale, company) {
			let file = bookings[0] || { fileNumber: null };
			let price = sumBy(bookings, 'price');
			sale.companyId = company.id;
			sale.fileNumber = file.fileNumber;
			const bookingId = this.getPaymentBookingId(bookings);
			if (bookingId) {
				sale.bookingId = bookingId;
			} else {
				delete sale.bookingId;
			}
			sale.amount = price;
			sale.customer.name = this.fileRequest.pax.name;
			sale.customer.address.countryId = this.fileRequest.pax.country.id;
			return this.paymentService.authorize(sale);
		},
		buildGuests() {
			let guests = [];
			const guestsQty = Number(this.search.guestsQty) || 1;
			for (let index = 0; index < guestsQty; index++) {
				guests.push({ name: null, birthDate: null });
			}
			return guests;
		},
		closeTransferDetails() {
			this.selectedTransfer = null;
			this.bookingDetails = null;
		},
		createBookingRequestItem(transfer) {
			const price = this.priceFor(transfer);
			const booking = new ServiceBooking();
			booking.serviceId = transfer.id;
			booking.serviceName = transfer.name;
			booking.date = this.search.date;
			booking.adultsQty = this.search.guestsQty;
			booking.price = price.price;
			booking.currency = price.priceCurrency;
			booking.guests = this.buildGuests();
			return booking;
		},
		cleanPaymentState() {
			this.fileRequest.serviceBookingsRequest = [];
			this.bookingResponse = null;
			this.selectedTransfer = null;
			this.bookingDetails = null;
		},
		getCountries() {
			this.paymentService.getCountries()
				.then(response => {
					this.countries = response.data.sort((a, b) => {
						if (a.name > b.name) return 1;
						if (a.name < b.name) return -1;
						return 0;
					});
				})
				.catch(() => {
					//alert
				});
		},
		generateBankTicket(bankTicket, bookings, company) {
			let file = bookings[0] || { fileNumber: null };
			let price = sumBy(bookings, 'price');
			bankTicket.fileNumber = file.fileNumber;
			const bookingId = this.getPaymentBookingId(bookings);
			if (bookingId) {
				bankTicket.bookingId = bookingId;
			} else {
				delete bankTicket.bookingId;
			}
			bankTicket.companyId = company.id;
			bankTicket.value = price;
			bankTicket.payerName = this.fileRequest.pax.name;
			return this.paymentService.generateBankTicket(bankTicket).then(res => res.data).catch(error => {
				console.log(error.response ? error.response.data.message : error);
			});
		},
		getErrorConfirmationRequest(hotelBooking) {
			return {
				pax: this.fileRequest.pax,
				fileNumber: hotelBooking.fileNumber,
				paidValue: hotelBooking.price,
			};
		},
		getPaymentBookingId(bookings) {
			if (bookings.length !== 1) return null;

			const bookingId = bookings[0] && bookings[0].bookingId;
			if (!bookingId) {
				throw new Error('A reserva de transfer não retornou o ID do booking.');
			}
			return bookingId.toString();
		},
		lowercaseLabel(label) {
			return String(label || '').toLowerCase();
		},
		isSelectedTransfer(transfer) {
			return Boolean(this.selectedTransfer && transfer && this.selectedTransfer.id === transfer.id);
		},
		openTransferDetails(transfer) {
			this.selectedTransfer = transfer;
			this.bookingDetails = this.createBookingRequestItem(transfer);
			this.$emit('select-transfer', {
				fileRequest: this.fileRequest,
				search: this.search,
				transfer,
			});
		},
		priceFor(transfer) {
			return transfer && transfer.prices && transfer.prices.length
				? transfer.prices[0]
				: { price: 0, pricePerPax: 0, priceCurrency: { symbol: 'R$' } };
		},
		async searchTransferServices() {
			const event = this.currentEvent();
			this.hasSearched = true;
			this.transfers = [];
			this.selectedTransfer = null;
			this.bookingDetails = null;

			if (this.$refs.waitModal) this.$refs.waitModal.open(this.translation.wait);

			try {
				const transfersResponse = await this.eventService.getTransfers(event.id);
				const eventTransfersIds = transfersResponse.data.map(eventTransfer => eventTransfer.service.id);

				if (!eventTransfersIds.length || !event.client) {
					return;
				}

				this.configureSearch(eventTransfersIds);
				const servicePricesWebservice = new ServicePricesWebservice(event.client);
				const response = await servicePricesWebservice.listTransferPrices(this.search);
				this.transfers = this.extractTransfersFromResponse(response.data.serviceTypes || []);
			} catch (error) {
				this.transfers = [];
			} finally {
				if (this.$refs.waitModal) this.$refs.waitModal.close();
			}
		},
		readableLabel(label) {
			return String(label || '')
				.toLowerCase()
				.replace(/(^|\s|-)\S/g, letter => letter.toUpperCase());
		},
		removeTransferBooking(bookingIndex) {
			if (bookingIndex < 0 || bookingIndex >= this.fileRequest.serviceBookingsRequest.length) return;
			this.fileRequest.serviceBookingsRequest.splice(bookingIndex, 1);
			this.bookingResponse = null;
			if (!this.hasBookings) {
				this.$nextTick(() => this.scrollToElement(this.$refs.searchPanel));
			}
		},
		scrollToElement(element) {
			if (!element || typeof element.scrollIntoView !== 'function') return;
			element.scrollIntoView({ behavior: 'smooth', block: 'center' });
		},
		async sendBookings(sale) {
			const event = this.currentEvent();
			if (this.$refs.waitModal) this.$refs.waitModal.open(this.translation.wait);

			try {
				const bookingService = new BookingService(event.client);

				if (!this.bookingResponse) {
					const fileRequestToSend = new FileRequest(
						this.fileRequest.pax,
						null,
						this.fileRequest.serviceBookingsRequest,
						'service',
					);
					this.bookingResponse = await bookingService.save(fileRequestToSend);
				}

				const { bookings, company } = this.bookingResponse.data;

				if (sale.brandId === this.PaymentMethods.BANK_TICKET.id) {
					const bankTicketGenerateResponse = await this.generateBankTicket(sale, bookings, company);
					if (
						!bankTicketGenerateResponse
						|| !bankTicketGenerateResponse.status
						|| Number(bankTicketGenerateResponse.status.id) === this.PaymentStatus.BANK_TICKET.NOT_ISSUED.id
					) {
						return this.$refs.invalidPaymentModal.open(this.PaymentMethods.BANK_TICKET);
					}

					let bankTicketUrl = null;
					try {
						const response = await this.paymentService.getBankTicketDocument(
							bankTicketGenerateResponse.id,
							bankTicketGenerateResponse.payerName,
						);
						bankTicketUrl = window.URL.createObjectURL(
							new Blob([response.data], { type: 'application/pdf' }),
						);
					} catch (error) {
						console.error('Erro ao gerar o documento do boleto.', error);
					}

					this.$refs.conclusionBankTicketModal.open(true, bankTicketUrl);
				} else {
					const authorizedSale = await this.authorizeCrediCardPayment(bookings, sale, company);
					if (!authorizedSale.data.status) {
						return this.$refs.invalidPaymentModal.open();
					}

					try {
						const confirmationResponse = await bookingService.confirm(bookings[0].fileNumber);
						if (!confirmationResponse.data.fullyUpdated) {
							throw new Error();
						}
					} catch (error) {
						const confirmationErrorRequest = this.getErrorConfirmationRequest(bookings[0]);
						this.eventService.sendConfirmationErrorEmail(event.id, confirmationErrorRequest);
					}

					this.$refs.conclusionModal.open(true);
				}

				this.cleanPaymentState();
			} catch (error) {
				console.error(error);
				this.$refs.conclusionModal.open(false);
			} finally {
				if (this.$refs.waitModal) this.$refs.waitModal.close();
			}
		},
		selectButtonLabel(transfer) {
			const label = this.isSelectedTransfer(transfer)
				? this.translation.detail.close
				: this.translation.detail.select;
			return this.lowercaseLabel(label);
		},
		toggleTransferDetails(transfer) {
			if (this.isSelectedTransfer(transfer)) {
				this.closeTransferDetails();
				return;
			}
			this.openTransferDetails(transfer);
		},
		transferName(transfer) {
			if (!transfer || !transfer.translations) return transfer && transfer.name ? transfer.name : '';
			const languageId = this.$store.state.language.id;
			const translation = transfer.translations.find(item => item.language.id === languageId)
				|| transfer.translations[0];
			return translation && translation.name ? translation.name : transfer.name;
		},
		updateFormattedDate() {
			if (!this.date) {
				this.search.date = null;
				return;
			}
			this.search.date = moment(this.date).format('DD/MM/YYYY');
		},
	},
};
</script>

<style scoped lang="scss">
.new-event-transfer-reservation {
	color: #002c41;
}

.new-event-transfer-reservation__inner {
	box-sizing: border-box;
	width: calc(100% - 196px);
	max-width: 1530px;
	margin: 0 auto 142px;
}

.new-event-transfer-reservation__inner h2 {
	margin: 0 0 43px;
	color: #002c41;
	font-size: 22px;
	font-weight: 900;
	line-height: 26px;
}

.new-event-transfer-reservation__form {
	display: flex;
	flex-direction: column;
	gap: 39px;
}

.new-event-transfer-reservation__panel {
	box-sizing: border-box;
	width: 100%;
	border: 1px solid #d7d7d7;
	border-radius: 35px;
}

.new-event-transfer-reservation__panel--holder {
	min-height: 314px;
	padding: 60px 61px 55px;
	background: #ffffff;
}

.new-event-transfer-reservation__panel--search {
	min-height: 269px;
	padding: 76px 61px 60px;
	background: #eeeeee;
}

.new-event-transfer-reservation__toggle {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 31px;
	height: 18px;
	margin: 0 0 22px;
	padding: 0;
	border: 0;
	background: transparent;
	cursor: default;
}

.new-event-transfer-reservation__toggle span {
	display: block;
	width: 17px;
	height: 17px;
	border-right: 3px solid #66acc6;
	border-bottom: 3px solid #66acc6;
	transform: rotate(45deg) translateY(-5px);
}

.new-event-transfer-reservation__panel h3 {
	margin: 0;
	color: #002c41;
	font-size: 18px;
	font-weight: 900;
	line-height: 21px;
}

.new-event-transfer-reservation__panel--holder h3 {
	margin-bottom: 42px;
}

.new-event-transfer-reservation__panel--search h3 {
	margin-bottom: 34px;
	font-size: 22px;
	line-height: 26px;
}

.new-event-transfer-reservation__holder-grid {
	display: flex;
	flex-wrap: wrap;
	gap: 17px 15px;
	max-width: 962px;
}

.new-event-transfer-reservation__field,
.new-event-transfer-reservation__passengers {
	position: relative;
	flex: 1 1 calc(50% - 8px);
	min-width: 0;
}

.new-event-transfer-reservation__field--full {
	flex-basis: 100%;
}

.new-event-transfer-reservation__field--select::after,
.new-event-transfer-reservation__passengers::after {
	content: "";
	position: absolute;
	top: 22px;
	right: 19px;
	width: 13px;
	height: 13px;
	border-right: 3px solid #66acc6;
	border-bottom: 3px solid #66acc6;
	pointer-events: none;
	transform: rotate(45deg) translateY(-4px);
}

.new-event-transfer-reservation__control,
.new-event-transfer-reservation__select,
.new-event-transfer-reservation__date /deep/ .datetime-input {
	display: block;
	box-sizing: border-box;
	width: 100%;
	height: 58px !important;
	border: 1px solid rgba(0, 0, 0, .2);
	border-radius: 10px;
	background-color: #ffffff;
	color: #5a5a5a;
	font-family: inherit;
	font-size: 18px;
	font-weight: 400;
	line-height: 18px;
	outline: none;
	box-shadow: none;
	transition: border-color .15s ease-in-out, box-shadow .15s ease-in-out;
}

.new-event-transfer-reservation__date /deep/ .datetime-input {
	padding-left: 12px;
}

.new-event-transfer-reservation__select {
	padding-right: 54px;
	background-image: none;
	appearance: none;
}

.new-event-transfer-reservation__control:focus,
.new-event-transfer-reservation__select:focus,
.new-event-transfer-reservation__date /deep/ .datetime-input:focus {
	border-color: #66acc6;
	box-shadow: 0 0 0 .2rem rgba(102, 172, 198, .15);
}

.new-event-transfer-reservation__control::placeholder,
.new-event-transfer-reservation__date /deep/ .datetime-input::placeholder {
	color: #5a5a5a;
	opacity: 1;
}

.new-event-transfer-reservation__search-row {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0 23px;
}

.new-event-transfer-reservation__search-row label {
	margin: 0;
	color: #5a5a5a;
	font-size: 18px;
	font-weight: 400;
	line-height: 18px;
}

.new-event-transfer-reservation__passengers {
	flex: 0 0 104px;
	width: 104px;
}

.new-event-transfer-reservation__passengers::before {
	content: "";
	position: absolute;
	top: 0;
	right: 49px;
	width: 1px;
	height: 57px;
	background: rgba(0, 0, 0, .1);
	z-index: 1;
}

.new-event-transfer-reservation__date {
	flex: 0 1 237px;
	width: 237px;
	max-width: 100%;
}

.new-event-transfer-reservation__date /deep/ .calendar-icon {
	width: 33px;
	right: 15px;
	bottom: 11px;
}

.new-event-transfer-reservation__button {
	box-sizing: border-box;
	width: 200px;
	height: 34px;
	margin-left: 14px;
	border: 1px solid #ff9600;
	border-radius: 10px;
	background: #ff9600;
	color: #ffffff;
	font-family: inherit;
	font-size: 16px;
	font-weight: 700;
	line-height: 19px;
	text-align: center;
	box-shadow: 4px 4px 100px rgba(0, 0, 0, .1);
	cursor: pointer;
}

.new-event-transfer-reservation__results {
	margin-top: 64px;
}

.new-event-transfer-reservation__results h3 {
	margin: 0 0 24px 52px;
	color: #002c41;
	font-size: 22px;
	font-weight: 900;
	line-height: 26px;
}

.new-event-transfer-reservation__result-list {
	display: flex;
	flex-direction: column;
	gap: 23px;
}

.new-event-transfer-reservation__result-item {
	display: flex;
	flex-direction: column;
}

.new-event-transfer-reservation__result-item--open .new-event-transfer-reservation__result {
	border-bottom: 0;
	border-radius: 35px 35px 0 0;
}

.new-event-transfer-reservation__result {
	display: grid;
	grid-template-columns: minmax(280px, 2.25fr) minmax(190px, 1fr) minmax(190px, 1fr) minmax(148px, .78fr);
	align-items: center;
	box-sizing: border-box;
	min-height: 126px;
	overflow: hidden;
	border: 1px solid #d7d7d7;
	border-radius: 35px;
	background: #ffffff;
	color: #002c41;
}

.new-event-transfer-reservation__result h4,
.new-event-transfer-reservation__price,
.new-event-transfer-reservation__result-action {
	display: flex;
	flex-direction: column;
	justify-content: center;
	box-sizing: border-box;
	min-height: 126px;
	margin: 0;
	padding: 24px 51px;
}

.new-event-transfer-reservation__price,
.new-event-transfer-reservation__result-action {
	border-left: 1px solid #d9d9d9;
}

.new-event-transfer-reservation__result h4 {
	font-size: 25px;
	font-weight: 900;
	line-height: 29px;
}

.new-event-transfer-reservation__price span {
	margin-bottom: 11px;
	font-size: 16px;
	font-weight: 400;
	line-height: 19px;
}

.new-event-transfer-reservation__price strong {
	font-size: 25px;
	font-weight: 900;
	line-height: 29px;
}

.new-event-transfer-reservation__result-action {
	align-items: center;
	padding-right: 44px;
	padding-left: 44px;
}

.new-event-transfer-reservation__select-button {
	box-sizing: border-box;
	width: 154px;
	height: 34px;
	border: 1px solid #ff9600;
	border-radius: 10px;
	background: #ff9600;
	color: #ffffff;
	font-family: inherit;
	font-size: 16px;
	font-weight: 700;
	line-height: 19px;
	text-align: center;
	box-shadow: 4px 4px 100px rgba(0, 0, 0, .1);
	cursor: pointer;
}

.new-event-transfer-reservation__select-button--outline {
	background: #ffffff;
	color: #ff9600;
}

.new-event-transfer-reservation__booking-details {
	box-sizing: border-box;
	min-height: 681px;
	padding: 36px 68px 77px;
	border-radius: 0 0 30px 30px;
	background: #eeeeee;
}

.new-event-transfer-reservation__booking-toggle {
	margin-bottom: 13px;
}

.new-event-transfer-reservation__booking-details h3 {
	margin: 0 0 54px;
	color: #002c41;
	font-size: 18px;
	font-weight: 700;
	line-height: 21px;
}

.new-event-transfer-reservation__booking-layout {
	display: flex;
	align-items: flex-end;
	gap: 40px;
}

.new-event-transfer-reservation__booking-fields {
	flex: 1 1 959px;
	width: 100%;
	max-width: 959px;
	min-width: 0;
}

.new-event-transfer-reservation__booking-row {
	margin-right: -6px;
	margin-bottom: 12px;
	margin-left: -6px;
}

.new-event-transfer-reservation__booking-row:last-child {
	margin-bottom: 0;
}

.new-event-transfer-reservation__booking-field {
	min-width: 0;
	padding-right: 6px;
	padding-left: 6px;
}

.new-event-transfer-reservation__textarea {
	height: 137px;
	min-height: 137px;
	padding-top: 16px;
	resize: none;
}

.new-event-transfer-reservation__passengers-details {
	display: flex;
	flex-direction: column;
	gap: 30px;
	margin-top: 49px;
}

.new-event-transfer-reservation__guest {
	min-width: 0;
	margin: 0;
	padding: 0;
	border: 0;
}

.new-event-transfer-reservation__guest legend {
	margin: 0 0 20px;
	color: #002c41;
	font-size: 18px;
	font-weight: 700;
	line-height: 21px;
}

.new-event-transfer-reservation__guest-grid {
	margin-right: -6px;
	margin-left: -6px;
}

.new-event-transfer-reservation__guest-field {
	min-width: 0;
	padding-right: 6px;
	padding-left: 6px;
}

.new-event-transfer-reservation__booking-date {
	width: 100%;
}

.new-event-transfer-reservation__booking-action {
	display: flex;
	flex: 0 0 154px;
	align-items: flex-end;
	justify-content: flex-end;
	margin-left: auto;
}

.new-event-transfer-reservation__add-button {
	box-sizing: border-box;
	width: 154px;
	height: 34px;
	margin-bottom: 8px;
	border: 1px solid #ff9600;
	border-radius: 10px;
	background: #ff9600;
	color: #ffffff;
	font-family: inherit;
	font-size: 16px;
	font-weight: 700;
	line-height: 19px;
	text-align: center;
	box-shadow: 4px 4px 100px rgba(0, 0, 0, .1);
	cursor: pointer;
}

.new-event-transfer-reservation__no-results {
	box-sizing: border-box;
	margin: 0;
	padding: 36px 52px;
	border: 1px solid #d7d7d7;
	border-radius: 35px;
	background: #ffffff;
	color: #002c41;
	font-size: 20px;
	font-weight: 700;
	line-height: 24px;
}

.new-event-transfer-reservation__checkout {
	width: 100%;
	max-width: 1123px;
	margin: 83px 0 0 auto;
}

@media (max-width: 991px) {
	.new-event-transfer-reservation__booking-row .new-event-transfer-reservation__booking-field + .new-event-transfer-reservation__booking-field,
	.new-event-transfer-reservation__guest-field + .new-event-transfer-reservation__guest-field {
		margin-top: 12px;
	}

	.new-event-transfer-reservation__booking-layout {
		flex-direction: column;
		align-items: stretch;
	}

	.new-event-transfer-reservation__booking-fields {
		max-width: none;
	}

	.new-event-transfer-reservation__booking-action {
		flex-basis: auto;
		justify-content: flex-start;
		margin-top: 24px;
		margin-left: 0;
		padding-left: 0;
	}
}

@media (max-width: 900px) {
	.new-event-transfer-reservation__inner {
		width: 90%;
		margin-bottom: 72px;
	}

	.new-event-transfer-reservation__inner h2 {
		margin-bottom: 28px;
	}

	.new-event-transfer-reservation__panel--holder,
	.new-event-transfer-reservation__panel--search {
		min-height: 0;
		padding: 38px 22px 42px;
		border-radius: 24px;
	}

	.new-event-transfer-reservation__holder-grid {
		max-width: none;
	}

	.new-event-transfer-reservation__field {
		flex-basis: 100%;
	}

	.new-event-transfer-reservation__field--full {
		flex-basis: 100%;
	}

	.new-event-transfer-reservation__search-row {
		align-items: flex-start;
		gap: 14px;
	}

	.new-event-transfer-reservation__search-row label {
		width: 100%;
	}

	.new-event-transfer-reservation__passengers,
	.new-event-transfer-reservation__date {
		width: 100%;
	}

	.new-event-transfer-reservation__date {
		flex: 1 1 100%;
	}

	.new-event-transfer-reservation__button {
		width: 100%;
		max-width: 200px;
		margin-left: 0;
	}

	.new-event-transfer-reservation__results {
		margin-top: 42px;
	}

	.new-event-transfer-reservation__results h3 {
		margin-left: 0;
		font-size: 20px;
		line-height: 24px;
	}

	.new-event-transfer-reservation__result {
		grid-template-columns: 1fr;
		border-radius: 24px;
	}

	.new-event-transfer-reservation__result-item--open .new-event-transfer-reservation__result {
		border-radius: 24px 24px 0 0;
	}

	.new-event-transfer-reservation__result h4,
	.new-event-transfer-reservation__price,
	.new-event-transfer-reservation__result-action {
		min-height: 0;
		padding: 20px 24px;
	}

	.new-event-transfer-reservation__price,
	.new-event-transfer-reservation__result-action {
		border-top: 1px solid #d9d9d9;
		border-left: 0;
	}

	.new-event-transfer-reservation__result h4,
	.new-event-transfer-reservation__price strong {
		font-size: 20px;
		line-height: 24px;
	}

	.new-event-transfer-reservation__price span {
		margin-bottom: 6px;
		font-size: 14px;
		line-height: 18px;
	}

	.new-event-transfer-reservation__result-action {
		align-items: flex-start;
	}

	.new-event-transfer-reservation__select-button {
		width: 100%;
		max-width: 154px;
	}

	.new-event-transfer-reservation__booking-details {
		min-height: 0;
		padding: 28px 24px 36px;
		border-radius: 0 0 24px 24px;
	}

	.new-event-transfer-reservation__booking-details h3 {
		margin-bottom: 28px;
	}

	.new-event-transfer-reservation__passengers-details {
		margin-top: 34px;
	}

	.new-event-transfer-reservation__add-button {
		width: 100%;
		max-width: 154px;
		margin-bottom: 0;
	}

	.new-event-transfer-reservation__no-results {
		padding: 26px 24px;
		border-radius: 24px;
		font-size: 17px;
		line-height: 22px;
	}

	.new-event-transfer-reservation__checkout {
		width: 100%;
		margin-top: 56px;
	}
}
</style>



// WEBPACK FOOTER //
// src/components/transfer-reservation/TransferPrice.vue